<template>
  <div class="min-h-screen bg-gray-50 p-6 pt-24 lg:pt-8">
    <div class="max-w-7xl mx-auto">
      <!-- Header -->
      <div class="mb-6">
        <h1 class="text-3xl font-bold text-gray-900" style="direction: rtl; text-align: right;">
          التقرير العام للفروع
        </h1>
        <p class="mt-2 text-sm text-gray-600" style="direction: rtl; text-align: right;">
          إحصائيات شاملة عن جميع الفروع
        </p>
      </div>

      <!-- Filters -->
      <div class="bg-white rounded-lg shadow p-6 mb-6" style="direction: rtl; text-align: right;">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              التاريخ (من - إلى)
            </label>
            <div dir="ltr" class="w-full">
              <VueDatePicker
                v-model="dateRange"
                :format="formatDate"
                range
                :max-date="new Date()"
                class="w-full"
                @update:model-value="onDateRangeChange"
              />
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              اختصارات سريعة
            </label>
            <select
              v-model="datePreset"
              @change="applyDatePreset"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
            >
              <option value="">اختر فترة</option>
              <option value="today">اليوم</option>
              <option value="last_week">آخر أسبوع</option>
              <option value="last_month">آخر شهر</option>
              <option value="last_year">آخر سنة</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              الفرع
            </label>
            <select
              v-model="selectedTenantId"
              @change="loadReport"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-customHafadhColor"
            >
              <option value="">جميع الفروع</option>
              <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
                {{ tenant.name }}
              </option>
            </select>
          </div>
          <div class="flex items-end">
            <button
              @click="loadReport"
              class="w-full bg-customHafadhColor text-white px-4 py-2 rounded-md hover:bg-customHafadhColorHover transition-colors"
            >
              تحديث التقرير
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6" v-if="reportData">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600" style="direction: rtl; text-align: right;">
                عدد الفروع
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2" style="direction: rtl; text-align: right;">
                {{ reportData.total_tenants || 0 }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-full">
              <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600" style="direction: rtl; text-align: right;">
                عدد الدارسين
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2" style="direction: rtl; text-align: right;">
                {{ reportData.total_students || 0 }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-full">
              <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600" style="direction: rtl; text-align: right;">
                عدد المعلمين
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2" style="direction: rtl; text-align: right;">
                {{ reportData.total_teachers || 0 }}
              </p>
            </div>
            <div class="p-3 bg-purple-100 rounded-full">
              <svg class="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600" style="direction: rtl; text-align: right;">
                عدد الجلسات
              </p>
              <p class="text-3xl font-bold text-gray-900 mt-2" style="direction: rtl; text-align: right;">
                {{ reportData.total_sessions || 0 }}
              </p>
            </div>
            <div class="p-3 bg-orange-100 rounded-full">
              <svg class="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-customHafadhColor"></div>
        <p class="mt-4 text-gray-600" style="direction: rtl; text-align: right;">جاري تحميل البيانات...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <p class="text-red-800" style="direction: rtl; text-align: right;">{{ error }}</p>
      </div>

      <!-- Tenants Table -->
      <div v-if="reportData && reportData.tenants && reportData.tenants.length > 0" class="bg-white rounded-lg shadow overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200" style="direction: rtl;">
            <thead class="bg-customHafadhColor">
              <tr>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  اسم الفرع
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  عدد الدارسين
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  عدد المعلمين
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  عدد الجلسات
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  الجلسات النشطة
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  عدد الجلسات اليوم
                </th>
                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                  الحالة
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="tenant in reportData.tenants" :key="tenant.tenant_id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {{ tenant.tenant_name }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ tenant.students_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ tenant.teachers_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ tenant.sessions_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ tenant.active_sessions_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ tenant.daily_trackers_count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    :class="tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                  >
                    {{ tenant.is_active ? 'نشط' : 'غير نشط' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="reportData && (!reportData.tenants || reportData.tenants.length === 0)" class="bg-white rounded-lg shadow p-12 text-center">
        <p class="text-gray-500" style="direction: rtl; text-align: right;">لا توجد بيانات للعرض</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import url from '../url';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  name: 'SuperAdminGeneralReport',
  components: {
    VueDatePicker,
  },
  data() {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    return {
      reportData: null,
      tenants: [],
      dateRange: [yesterday, today],
      datePreset: '',
      selectedTenantId: '',
      loading: false,
      error: null,
    };
  },
  mounted() {
    this.loadTenants();
    this.loadReport();
  },
  methods: {
    formatDate(date) {
      if (!date || !Array.isArray(date) || date.length !== 2) {
        return 'اختر التاريخ';
      }
      const format = (d) => {
        if (!d) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      return `من ${format(date[0])} إلى ${format(date[1])}`;
    },
    onDateRangeChange() {
      this.datePreset = '';
      if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        this.loadReport();
      }
    },
    applyDatePreset() {
      if (!this.datePreset) return;
      
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      let startDate = new Date();
      
      switch (this.datePreset) {
        case 'today':
          startDate = new Date(today);
          startDate.setHours(0, 0, 0, 0);
          this.dateRange = [startDate, today];
          break;
        case 'last_week':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          this.dateRange = [startDate, today];
          break;
        case 'last_month':
          startDate = new Date(today);
          startDate.setMonth(startDate.getMonth() - 1);
          startDate.setHours(0, 0, 0, 0);
          this.dateRange = [startDate, today];
          break;
        case 'last_year':
          startDate = new Date(today);
          startDate.setFullYear(startDate.getFullYear() - 1);
          startDate.setHours(0, 0, 0, 0);
          this.dateRange = [startDate, today];
          break;
      }
      
      this.loadReport();
    },
    async loadTenants() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(url.get_tenants, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data && response.data.result && response.data.result.data) {
          this.tenants = response.data.result.data.tenants || [];
        }
      } catch (error) {
        console.error('Error loading tenants:', error);
      }
    },
    async loadReport() {
      this.loading = true;
      this.error = null;
      try {
        const token = localStorage.getItem('token');
        const params = {};
        
        // Add date range if available
        if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
          const formatDate = (date) => {
            if (!date) return null;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          };
          params.start_date = formatDate(this.dateRange[0]);
          params.end_date = formatDate(this.dateRange[1]);
        }
        
        if (this.selectedTenantId) {
          params.tenant_id = this.selectedTenantId;
        }
        
        const response = await axios.get(url.super_admin_general_report, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        });
        if (response.data && response.data.result && response.data.result.data) {
          this.reportData = response.data.result.data;
        } else {
          this.error = 'فشل في تحميل البيانات';
        }
      } catch (error) {
        console.error('Error loading report:', error);
        this.error = error.response?.data?.result?.mesAr || 'حدث خطأ أثناء تحميل التقرير';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Add any custom styles here */
</style>

